import { maskEmailAddress, maskPersonName, maskPhoneNumber } from '../../../utils/privacyMasking';
import { CUSTOMER_MATCH_REASON_LABEL, type CustomerSearchRow } from '../../../services/crmFindCustomersService';
import { resolveCrmEntityRoute } from '../../../utils/crmRouteResolver';

/** Presentation helpers shared by the Customers directory table and the CRM home search results. */

const DAY_MS = 24 * 60 * 60 * 1000;

export function customerRowHref(row: Pick<CustomerSearchRow, 'entityType' | 'id'> & { number?: number | null }): string {
  const targetId = row.entityType === 'customer' && row.number != null ? String(row.number) : row.id;
  return resolveCrmEntityRoute(row.entityType === 'company' ? 'company' : 'customer', targetId);
}

export function customerRowName(row: Pick<CustomerSearchRow, 'entityType' | 'name'>, privacy: boolean): string {
  if (row.entityType === 'company' || !privacy) return row.name;
  return maskPersonName(row.name);
}

export function customerRowNumber(row: Pick<CustomerSearchRow, 'number'>, privacy: boolean): string | null {
  if (row.number === null) return null;
  return privacy ? '#••••' : `#${row.number}`;
}

export function customerRowPhone(phone: string | null, privacy: boolean): string | null {
  if (!phone) return null;
  return privacy ? maskPhoneNumber(phone) : phone;
}

export function customerRowEmail(email: string | null, privacy: boolean): string | null {
  if (!email) return null;
  return privacy ? maskEmailAddress(email) : email;
}

export function customerRowOwner(row: Pick<CustomerSearchRow, 'entityType' | 'ownerName'>, privacy: boolean): string {
  if (row.entityType === 'company') return '—';
  if (!row.ownerName) return 'Not recorded';
  return privacy ? maskPersonName(row.ownerName) : row.ownerName;
}

/** "Booking code ABC123" / "Part of phone" — why this row matched the search. */
export function customerMatchLabel(row: Pick<CustomerSearchRow, 'matchReason' | 'matchedValue'>, privacy: boolean): string | null {
  if (!row.matchReason) return null;
  const label = CUSTOMER_MATCH_REASON_LABEL[row.matchReason];
  if (row.matchReason === 'booking_code' && row.matchedValue) {
    return privacy ? `${label} (hidden)` : `${label} ${row.matchedValue}`;
  }
  return label;
}

function parseDay(value: string): Date | null {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDay(value: string | null): string | null {
  if (!value) return null;
  const date = parseDay(value);
  if (!date) return null;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

/** "Away now (back 22 Oct)" when a customer has departed and not yet returned. */
export function formatAwayNowLabel(returnDate: string | null | undefined): string | null {
  if (!returnDate) return null;
  const date = parseDay(returnDate);
  if (!date) return null;
  const shortDay = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(date);
  return `Away now (back ${shortDay})`;
}

/** "today", "in 3 days", "in 5 weeks". */
export function formatTripDistance(value: string | null, now: Date = new Date()): string | null {
  if (!value) return null;
  const date = parseDay(value);
  if (!date) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((date.getTime() - today.getTime()) / DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 21) return `in ${days} days`;
  if (days < 120) return `in ${Math.round(days / 7)} weeks`;
  return `in ${Math.round(days / 30)} months`;
}

/** "12 min ago", "3 h ago", "4 days ago", or the date for anything older than 60 days. */
export function formatSince(value: string | null, now: Date = new Date()): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const minutes = Math.max(0, Math.round((now.getTime() - date.getTime()) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 60) return days === 1 ? 'yesterday' : `${days} days ago`;
  return formatDay(value);
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value);
}
